<template>
    <div class="scan-page">

        <template v-if="repairKit">
            <div class="alert alert-info alert-centered rounded-0 mb-0" v-if="repairKit.in_house_technician">
                <animated-info></animated-info> In-house repair
            </div>

            <!-- Print label prompt -->
            <div class="col-12 col-md-6 offset-md-3 col-lg-4 offset-lg-4" v-if="!repairKitLabelPrintingSkipped && !repairKit.label_printed">
                <empty-state
                        class="mt-5"
                        fullscreen
                        icon="printer"
                        title="Print the order label to get started">
                    <slot>
                        <button class="btn btn-info btn-lg btn-block mt-4" @click="printRepairKitLabel(repairKitId)">Print kit label</button>
                        <button class="btn btn-link btn-block" @click="skipRepairKitLabelPrinting">Continue without printing</button>
                    </slot>
                </empty-state>
            </div>
        </template>

        <!-- Barcode picker -->
        <barcode-picker
                @barcodeHasBeenScanned="addDetailToRepairKit"
                ref="barcodePicker"
                :resumeDelay="500"
                v-show="repairKit && (repairKitLabelPrintingSkipped || repairKit.label_printed)"
        ></barcode-picker>

        <template v-if="repairKit && (repairKitLabelPrintingSkipped || repairKit.label_printed)">
            <!-- Details -->
            <div class="scan-details">
                <div class="container">
                    <h4 class="d-flex align-items-center mb-2">Repair kit {{repairKitId}} <button class="btn btn-info btn-sm ml-auto" @click="printRepairKitLabel(repairKitId)"><template v-if="repairKit.label_printed">Reprint</template><template v-if="!repairKit.label_printed">Print</template> kit label</button></h4>
                    <h5 class="text-subtitle mb-0">{{detailsPicked.length}} of {{repairKitDetails.length}} picked</h5>
                </div>
            </div>

            <!-- Item list -->
            <div class="container scan-content">
                <b-tabs>

                    <!-- To be picked -->
                    <b-tab title="To be picked" active>
                        <div class="card">
                            <ul class="checklist my-0" v-if="detailsToBePicked.length > 0">
                                <li
                                        v-for="detail in detailsToBePicked"
                                        :key="detail.id"
                                        :class="{'complete': detailComplete(detail)}"
                                        class="checklist-item no-hover">
                                    {{ detailTitle(detail) }}
                                </li>
                            </ul>
                            <empty-state
                                    v-if="detailsToBePicked.length < 1"
                                    emptyIcon
                                    icon="stock-pick"
                                    inline
                                    small
                                    title=""
                                    subtitle="No items to be picked">
                            </empty-state>
                        </div>
                    </b-tab>

                    <!-- Picked -->
                    <b-tab title="Picked">
                        <div class="card">
                            <ul class="checklist my-0" v-if="detailsPicked.length > 0">
                                <li
                                        v-for="detail in detailsPicked"
                                        :key="detail.id"
                                        :class="{'complete': detailComplete(detail)}"
                                        class="checklist-item no-hover">
                                    {{ detailTitle(detail) }}
                                </li>
                            </ul>
                            <empty-state
                                    v-if="detailsPicked.length < 1"
                                    emptyIcon
                                    icon="stock-pick"
                                    inline
                                    small
                                    title=""
                                    subtitle="No items have been picked">
                            </empty-state>
                        </div>
                    </b-tab>

                    <!-- All items -->
                    <b-tab title="All items">
                        <div class="card">
                            <ul class="checklist my-0" v-if="repairKitDetails.length > 0">
                                <li
                                        v-for="detail in repairKitDetails"
                                        :key="detail.id"
                                        :class="{'complete': detailComplete(detail)}"
                                        class="checklist-item no-hover">
                                    {{ detailTitle(detail) }}
                                </li>
                            </ul>
                            <empty-state
                                    v-if="repairKitDetails.length < 1"
                                    emptyIcon
                                    icon="stock-pick"
                                    inline
                                    small
                                    title=""
                                    subtitle="No items in this repair kit">
                            </empty-state>
                        </div>
                    </b-tab>
                </b-tabs>
            </div>
        </template>
    </div>
</template>

<script>
    import {mapActions, mapGetters} from "vuex";

    export default {
        props:['repairKitId'],

        data() {
            return {
                repairKitLabelPrintingSkipped: false,
                repeatScanningCheck: 0
            }
        },

        mounted() {
            this.repeatScanningCheck = 0;

            this.loadRepairKit({
                repairKitId: this.repairKitId
            });

            this.loadRepairKitDetails({
                repairKitId: this.repairKitId
            }).then(() => {
                this.isPickingComplete();
            });

        },

        computed: {
            ...mapGetters([
                'repairKit',
                'repairKitDetails',
                'detailsPicked',
                'detailsToBePicked'
            ]),
        },

        methods: {
            ...mapActions([
                'displayToast',
                'loadRepairKit',
                'loadRepairKitDetails',
                'printRepairKitLabel',
                'repairKitDetailBarcodeScanned',
                'setRepairKitAsPicked',
                'destroyBarcodeScanner'
            ]),

            /** Add scanned detail */
            addDetailToRepairKit(barcodeScanned) {
                this.repairKitDetailBarcodeScanned(barcodeScanned)
                    .then(() => {
                        this.displayToast({text: "Scanned!", type: 'success', duration: 2000});
                        this.repeatScanningCheck = 0;
                        this.isPickingComplete();
                    })
                    .catch(message => {
                        this.repeatScanningCheck++;
                        if (this.repeatScanningCheck > 1) {
                            this.displayToast({text: message, type: 'error'});
                        }
                    });
            },

            /** Check if picking is complete */
            isPickingComplete() {
                if (this.detailsPicked.length === this.repairKitDetails.length) {
                    this.setRepairKitAsPicked(this.repairKitId);
                    this.$router.push({name: 'repairKitPicking', params:{kitPicked:true}});
                }
            },

            /** Skip initial printing of the label */
            skipRepairKitLabelPrinting() {
                this.repairKitLabelPrintingSkipped = true;
            },

            /** Return true if the detail has been picked */
            detailComplete(detail) {
                return (detail.stock_serial_number_id || detail.picked);
            },

            detailTitle(detail) {
                if (detail.type === 'jobstockitem') {
                    return detail.stock_item_title;
                }
                if (detail.type === 'asset') {
                    return `Asset: ${detail.asset_day_number} (${detail.asset_reference})`;
                }
                return 'ERROR'
            }
        }
    }
</script>

<style scoped>

</style>